import { Badge } from "../../components/ui/badge";
import { Box } from "../../components/ui/box";
import { DrawerTrigger } from "../../components/ui/drawer";
import { Icon, type GlyphName } from "../../components/ui/icon";
import { PopoverTrigger } from "../../components/ui/popover";
import { selectTriggerVariants } from "../../components/ui/select";
import { Text } from "../../components/ui/text";
import { cn } from "../../lib/utils";
import { EntityGroup, type EntityGroupItem } from "../entity-group";
import * as React from "react";
import { useEntitySelector } from "./entity-selector-context";
import type { Omittable } from "../../lib/props";

export type EntitySelectorTriggerSize = "sm" | "default" | "lg";

export type EntitySelectorTriggerProps = Omittable<{
  placeholder?: string;
  /** Accessible name for the trigger. A combobox is named by its label, never
   *  by the value it shows, so this defaults to `placeholder` — the one string
   *  the trigger already carries that says what is being picked. A custom
   *  `render` trigger gets it only when it is given: with no label, that
   *  element keeps the name its own content gives it. */
  "aria-label"?: string;
  disabled?: boolean;
  size?: EntitySelectorTriggerSize;
  /** When true, the trigger stretches to fill its container width. @default false */
  fill?: boolean;
  /** Show removable chips for each selection instead of summary text. @default false */
  chips?: boolean;
  /** Show first selection's avatar + name with a "+N" badge for additional selections. Mutually exclusive with `chips`. @default false */
  summary?: boolean;
  /** Leading icon rendered before the placeholder/value. */
  icon?: GlyphName;
  /**
   * Center the leading avatar/icon in a fixed-width slot so value text aligns
   * to a constant column across a stack of controls with differently-sized
   * glyphs. Controls with no glyph render no slot — text hugs the leading edge
   * rather than sitting after an empty gutter. @default false
   */
  leadingGutter?: boolean;
  /**
   * Transparent variant: drops the filled background + shadow (keeps the border
   * and chevron) so the trigger reads as a quiet inline field. @default false
   */
  ghost?: boolean;
  /**
   * Fills with `muted` in LIGHT mode only (dark is unchanged) so this trigger
   * reads as the primary control in a stack of fields. The page is already white
   * in light mode, so a fill is the only thing that separates it there; in dark
   * the surface ladder already does that work. @default false
   */
  emphasized?: boolean;
  /**
   * Strengthen the border on hover, for a trigger that must advertise itself as
   * editable before it is touched. `ghost` gets this for free — with no fill or
   * shadow, the border is the only thing left to change. @default false
   */
  hoverBorder?: boolean;
  /** Weight of the value text. Derived from `Text` so it cannot drift. */
  weight?: React.ComponentProps<typeof Text>["weight"];
  /** Custom trigger element, passed through to Base UI's `render`. */
  render?: React.ReactElement<Record<string, unknown>>;
}>;

// Fixed-width leading slot so value text aligns across stacked controls.
const LEADING_GUTTER_CLASS = "w-5";

function EntitySelectorTrigger({
  placeholder = "Select...",
  "aria-label": ariaLabel,
  disabled = false,
  size,
  fill = false,
  chips = false,
  summary = false,
  icon,
  leadingGutter = false,
  ghost = false,
  emphasized = false,
  hoverBorder = false,
  weight,
  render,
}: EntitySelectorTriggerProps) {
  const { selections, toggleSelection, open, variant } =
    useEntitySelector();

  const displayText = React.useMemo(() => {
    const [first] = selections;
    if (first === undefined) return null;
    if (selections.length === 1) return first.entity.title;
    return `${selections.length} selected`;
  }, [selections]);

  const showChips = chips && selections.length > 0;
  const showSummary = !chips && summary && selections.length > 0;

  const groupItems = React.useMemo<EntityGroupItem[]>(() => {
    if (!showSummary) return [];
    return selections.map((sel) => {
      const avatar = sel.entity.avatar;
      const avatarIcon = avatar?.icon;
      const iconName = (
        typeof avatarIcon === "string" ? avatarIcon : undefined
      ) as GlyphName | undefined;
      return {
        id: sel.id,
        name: sel.entity.title,
        icon: iconName ?? null,
        iconColor: avatar?.color ?? null,
      };
    });
  }, [showSummary, selections]);

  const singleAvatar = React.useMemo(() => {
    const only = selections.length === 1 ? selections[0] : undefined;
    const avatar = only?.entity.avatar;
    if (!avatar) return null;
    // Prefer a custom-rendered visual (e.g. a stage's pie icon) so the
    // selected value in the trigger matches the option rows; otherwise fall
    // back to a primitive icon.
    if (avatar.render) return avatar.render();
    const iconName = (
      typeof avatar.icon === "string" ? avatar.icon : undefined
    ) as GlyphName | undefined;
    if (!iconName) return null;
    return <Icon name={iconName} size="md" />;
  }, [selections]);

  // Inline mode has no trigger — content is always visible
  if (variant === "inline") return null;

  const TriggerPart = variant === "drawer" ? DrawerTrigger : PopoverTrigger;

  if (render) {
    return (
      <TriggerPart
        disabled={disabled}
        render={render}
        {...(ariaLabel ? { "aria-label": ariaLabel } : {})}
      />
    );
  }

  const showPlaceholder = !displayText;

  const glyph =
    singleAvatar ??
    (icon ? <Icon name={icon} size="sm" color="muted" /> : null);

  return (
    <TriggerPart
      disabled={disabled}
      // The default chrome hosts nested interactive elements (chip remove
      // buttons), so the trigger itself must not be a native button element.
      nativeButton={false}
      render={
        <Box
          direction="row"
          align="center"
          role="combobox"
          aria-label={ariaLabel ?? placeholder}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={cn(
            selectTriggerVariants({ size: size ?? "default" }),
            // Deltas from the Select trigger: no fixed min width (the trigger
            // sizes to its flex context), and chips scroll in their own row so
            // the shell's nowrap is dropped there.
            !fill && "w-auto min-w-0",
            ghost &&
              "bg-transparent shadow-none before:hidden dark:bg-transparent",
            emphasized && "not-dark:bg-muted",
            (hoverBorder || ghost) && "hover:border-muted-foreground/60",
            showChips && "whitespace-normal",
            disabled && "pointer-events-none opacity-64",
          )}
        />
      }
    >
      {showChips ? (
        <>
          <Box overflowX="auto"
            direction="row"
            align="center"
            gap="xs"
            flex={1}
            minWidth={0}
            scrollbar="hidden"
          >
            {selections.map((sel) => (
              <Badge key={sel.id} size="sm" color="secondary">
                <Box direction="row" align="center" gap="xs">
                  <Text size="xs" variant="muted">
                    {sel.entity.title}
                  </Text>
                  <Box
                    as="button"
                    onClick={(e: React.MouseEvent<HTMLElement>) => {
                      e.stopPropagation();
                      toggleSelection(sel.entity, sel.poolId);
                    }}
                    aria-label={`Remove ${sel.entity.title}`}
                  >
                    <Icon name="x" size="md" color="muted" />
                  </Box>
                </Box>
              </Badge>
            ))}
          </Box>
          <Icon name="chevron-down" size="md" color="muted" />
        </>
      ) : showSummary ? (
        <>
          <Box direction="row" align="center" flex={1} minWidth={0}>
            <EntityGroup items={groupItems} variant="expanded" size="sm" />
          </Box>
          <Icon name="chevron-down" size="md" color="muted" />
        </>
      ) : (
        <>
          {leadingGutter && glyph ? (
            <Box
              direction="row"
              align="center"
              justify="center"
              shrink={false}
              className={LEADING_GUTTER_CLASS}
            >
              {glyph}
            </Box>
          ) : (
            glyph
          )}
          <Text
            size="sm"
            truncate
            weight={weight}
            className={cn(
              "flex-1 leading-4",
              showPlaceholder && "text-muted-foreground",
            )}
          >
            {displayText ?? placeholder}
          </Text>
          <Icon name="chevron-down" size="md" color="muted" />
        </>
      )}
    </TriggerPart>
  );
}

EntitySelectorTrigger.displayName = "EntitySelectorTrigger";

export { EntitySelectorTrigger };
